import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { Contract } from "./models/Contract";
import {
  cancelContract,
  createContract,
  fetchContracts,
  holdContract,
  PaginatedContracts,
  requestContractAcceptance,
  resumeContract,
  updateContract,
} from "./slice-extensions/contracts-slice-extension";

interface ContractsState {
  items: Contract[];
  page: number;
  pageSize: number;
  totalCount: number;
  status: LoadingStatus;
  saveStatus: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: ContractsState = {
  items: [],
  page: 1,
  pageSize: 20,
  totalCount: 0,
  status: LoadingStatus.Idle,
  saveStatus: LoadingStatus.Idle,
  lastError: undefined,
};

const upsert = (items: Contract[], updated: Contract): Contract[] =>
  items.some((i) => i.id === updated.id)
    ? items.map((i) => (i.id === updated.id ? updated : i))
    : [updated, ...items];

const contractsSlice = createSlice({
  name: "contracts",
  initialState,
  reducers: {
    clearContractsError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContracts.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchContracts.fulfilled, (state, action: PayloadAction<PaginatedContracts>) => {
        state.status = LoadingStatus.Idle;
        state.items = action.payload.items;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchContracts.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(createContract.pending, (state) => {
        state.saveStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(createContract.fulfilled, (state, action: PayloadAction<Contract>) => {
        state.saveStatus = LoadingStatus.Idle;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createContract.rejected, (state, action) => {
        state.saveStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(updateContract.fulfilled, (state, action: PayloadAction<Contract>) => {
        state.items = upsert(state.items, action.payload);
      })
      .addCase(holdContract.fulfilled, (state, action: PayloadAction<Contract>) => {
        state.items = upsert(state.items, action.payload);
      })
      .addCase(resumeContract.fulfilled, (state, action: PayloadAction<Contract>) => {
        state.items = upsert(state.items, action.payload);
      })
      .addCase(cancelContract.fulfilled, (state, action: PayloadAction<Contract>) => {
        state.items = upsert(state.items, action.payload);
      })
      .addCase(requestContractAcceptance.fulfilled, (state, action: PayloadAction<Contract>) => {
        state.items = upsert(state.items, action.payload);
      })

      .addCase(updateContract.rejected, (state, action) => {
        state.lastError = action.payload;
      })
      .addCase(holdContract.rejected, (state, action) => {
        state.lastError = action.payload;
      })
      .addCase(resumeContract.rejected, (state, action) => {
        state.lastError = action.payload;
      })
      .addCase(cancelContract.rejected, (state, action) => {
        state.lastError = action.payload;
      })
      .addCase(requestContractAcceptance.rejected, (state, action) => {
        state.lastError = action.payload;
      });
  },
});

export const { clearContractsError } = contractsSlice.actions;

export const selectContracts = (state: RootState) => state.contracts.items;
export const selectContractsByMember = (memberId: string) => (state: RootState) =>
  state.contracts.items.filter((c) => c.memberId === memberId);
export const selectContractsStatus = (state: RootState) => state.contracts.status;
export const selectContractsError = (state: RootState) => state.contracts.lastError;

export default contractsSlice.reducer;
